import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { NL_SEARCH_MODES } from '../../../utils/naturalLanguage';
import { QUERY_TAB, QUERY_TAB_RECOMMENDATIONS } from '../../../constants/queries';
import { ANY_OF_THOSE } from '../../../constants/filter';

const STATE_NAME_TO_CODE = {
  alabama: 'AL',
  alaska: 'AK',
  arizona: 'AZ',
  arkansas: 'AR',
  california: 'CA',
  colorado: 'CO',
  connecticut: 'CT',
  delaware: 'DE',
  'district of columbia': 'DC',
  florida: 'FL',
  georgia: 'GA',
  hawaii: 'HI',
  idaho: 'ID',
  illinois: 'IL',
  indiana: 'IN',
  iowa: 'IA',
  kansas: 'KS',
  kentucky: 'KY',
  louisiana: 'LA',
  maine: 'ME',
  maryland: 'MD',
  massachusetts: 'MA',
  michigan: 'MI',
  minnesota: 'MN',
  mississippi: 'MS',
  missouri: 'MO',
  montana: 'MT',
  nebraska: 'NE',
  nevada: 'NV',
  'new hampshire': 'NH',
  'new jersey': 'NJ',
  'new mexico': 'NM',
  'new york': 'NY',
  'north carolina': 'NC',
  'north dakota': 'ND',
  ohio: 'OH',
  oklahoma: 'OK',
  oregon: 'OR',
  pennsylvania: 'PA',
  'rhode island': 'RI',
  'south carolina': 'SC',
  'south dakota': 'SD',
  tennessee: 'TN',
  texas: 'TX',
  utah: 'UT',
  vermont: 'VT',
  virginia: 'VA',
  washington: 'WA',
  'west virginia': 'WV',
  wisconsin: 'WI',
  wyoming: 'WY',
  'puerto rico': 'PR',
  'virgin islands': 'VI',
  guam: 'GU',
  'american samoa': 'AS',
  'northern mariana islands': 'MP',
};

const SET_ASIDE_MAP = {
  wosb: { id: 'WOSB', multipleId: ['WOSB', '8W'] },
  'women-owned small business': { id: 'WOSB', multipleId: ['WOSB', '8W'] },
  'women-owned small business (wosb)': { id: 'WOSB', multipleId: ['WOSB', '8W'] },
  'women-owned small business (wosb) program set-aside (far 19.15)': { id: 'WOSB', multipleId: ['WOSB', '8W'] },

  edwosb: { id: 'EDWOSB', multipleId: ['EDWOSB'] },
  'economically disadvantaged women-owned small business': { id: 'EDWOSB', multipleId: ['EDWOSB'] },
  'economically disadvantaged wosb': { id: 'EDWOSB', multipleId: ['EDWOSB'] },
  'economically disadvantaged wosb (edwosb) program set-aside (far 19.15)': { id: 'EDWOSB', multipleId: ['EDWOSB'] },

  sdvosb: { id: 'SDVOSB', multipleId: ['SDVOSBC', 'SDVOSB', 'QF'] },
  'service-disabled veteran-owned small business': { id: 'SDVOSB', multipleId: ['SDVOSBC', 'SDVOSB', 'QF'] },
  'service-disabled veteran-owned small business (sdvosb) set-aside (far 19.14)': {
    id: 'SDVOSB',
    multipleId: ['SDVOSBC', 'SDVOSB', 'QF'],
  },

  vosb: { id: 'VSA', multipleId: ['VSA', 'A5'] },
  vsa: { id: 'VSA', multipleId: ['VSA', 'A5'] },
  'veteran-owned small business': { id: 'VSA', multipleId: ['VSA', 'A5'] },
  'veteran-owned small business set-aside (specific to department of veterans affairs)': {
    id: 'VSA',
    multipleId: ['VSA', 'A5'],
  },

  sb: { id: 'SBA', multipleId: ['SBA', 'Total Small Business Set-Aside (FAR 19.5)'] },
  sba: { id: 'SBA', multipleId: ['SBA', 'Total Small Business Set-Aside (FAR 19.5)'] },
  'small business': { id: 'SBA', multipleId: ['SBA', 'Total Small Business Set-Aside (FAR 19.5)'] },
  'total small business set-aside (far 19.5)': {
    id: 'SBA',
    multipleId: ['SBA', 'Total Small Business Set-Aside (FAR 19.5)'],
  },

  hubzone: { id: 'HZC', multipleId: ['HZC', 'XX'] },
  hzc: { id: 'HZC', multipleId: ['HZC', 'XX'] },
  'historically underutilized business (hubzone) set-aside (far 19.13)': { id: 'HZC', multipleId: ['HZC', 'XX'] },

  '8a': { id: '8A', multipleId: ['8A', 'A6'] },
  '8(a)': { id: '8A', multipleId: ['8A', 'A6'] },
  '8(a) set-aside (far 19.8)': { id: '8A', multipleId: ['8A', 'A6'] },
};

/**
 * Normalizes a raw criteria value (e.g. "236220 - Commercial Building", "Florida",
 * "Women-Owned Small Business", or a code object) into a clean { id, value } pair
 * where id is the queryable code (e.g. "236220", "FL", "WOSB") and value is the
 * human display label.
 */
const isAlphaNumericCode = (val) => {
  if (!val || val.length < 2 || val.length > 6) return false;
  for (let i = 0; i < val.length; i += 1) {
    const code = val.codePointAt(i);
    const isNum = code >= 48 && code <= 57;
    const isUpper = code >= 65 && code <= 90;
    const isLower = code >= 97 && code <= 122;
    if (!isNum && !isUpper && !isLower) return false;
  }
  return true;
};

const extractCodeFromHyphen = (str) => {
  const separators = [' - ', ' – ', ' — ', ' -', ' –', ' —', '- ', '– ', '— ', '-', '–', '—'];
  for (const sep of separators) {
    const idx = str.indexOf(sep);
    if (idx !== -1) {
      const candidate = str.slice(0, idx).trim();
      if (isAlphaNumericCode(candidate)) {
        return candidate;
      }
    }
  }
  return null;
};

export const extractCodeAndLabel = (raw) => {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object') {
    const rawCode = raw.id || raw.code || raw.value;
    const label = raw.value || raw.name || raw.label || rawCode;
    const parsed = extractCodeAndLabel(rawCode);
    if (parsed) {
      return {
        ...parsed,
        value: String(label).trim(),
        ...(raw.multipleId ? { multipleId: raw.multipleId } : {}),
      };
    }
    return null;
  }
  const str = String(raw).trim();
  const lower = str.toLowerCase();
  if (SET_ASIDE_MAP[lower]) {
    return { ...SET_ASIDE_MAP[lower], value: str };
  }
  if (STATE_NAME_TO_CODE[lower]) {
    return { id: STATE_NAME_TO_CODE[lower], value: str };
  }
  const matchedCode = extractCodeFromHyphen(str);
  if (matchedCode) {
    return { id: matchedCode, value: str };
  }
  return { id: str, value: str };
};

/**
 * Builds a SearchTopPanel filter selection from a list of raw values (NAICS codes, PSC codes, state codes).
 * Normalizes composite labels ("236220 - Description") and full state names to clean query codes,
 * deduplicating by code id. Returns null when values are empty or null so callers can omit the filter key entirely.
 */
export const toFilterSelection = (values) => {
  let list = [];
  if (Array.isArray(values)) {
    list = values;
  } else if (values != null && values !== '') {
    list = [values];
  }
  const seen = new Set();
  const data = [];
  list.forEach((item) => {
    const parsed = extractCodeAndLabel(item);
    if (parsed && !seen.has(parsed.id)) {
      seen.add(parsed.id);
      data.push(parsed);
    }
  });
  return data.length ? { type: ANY_OF_THOSE, data } : null;
};

/**
 * Reusable recommendations tab hook for opportunity, forecast, and vendor listings:
 * - Detects ?tab=recommendations deep-link on mount.
 * - Synchronizes active searchMode with query parameters.
 * - Automatically resets to Filtered mode if recommendations become disabled on the active view.
 * - Handles the Refine Recommendations handoff to the caller's custom filter mapping.
 */
export default function useRecommendationsTab({
  query,
  setQuery,
  deleteQuery,
  recommendationsEnabled = false,
  onRefine,
}) {
  const deepLinkActive = query?.[QUERY_TAB] === QUERY_TAB_RECOMMENDATIONS;
  const [searchMode, setSearchMode] = useState(
    recommendationsEnabled && deepLinkActive ? NL_SEARCH_MODES.RECOMMENDATIONS : NL_SEARCH_MODES.FILTERED
  );
  const isRecommendationsMode = searchMode === NL_SEARCH_MODES.RECOMMENDATIONS;
  const recommendationsMatchingCriteria = useSelector((state) => state?.recommendations?.matchingCriteria);

  const handleSearchModeChange = (nextMode) => {
    setSearchMode(nextMode);
    if (nextMode === NL_SEARCH_MODES.RECOMMENDATIONS) {
      if (typeof setQuery === 'function') {
        setQuery({ [QUERY_TAB]: QUERY_TAB_RECOMMENDATIONS })();
      }
    } else if (query?.[QUERY_TAB] === QUERY_TAB_RECOMMENDATIONS && typeof deleteQuery === 'function') {
      deleteQuery(QUERY_TAB)();
    }
  };

  useEffect(() => {
    if (!recommendationsEnabled && searchMode === NL_SEARCH_MODES.RECOMMENDATIONS) {
      handleSearchModeChange(NL_SEARCH_MODES.FILTERED);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendationsEnabled]);

  const handleRefineRecommendations = () => {
    handleSearchModeChange(NL_SEARCH_MODES.FILTERED);
    if (typeof onRefine === 'function') {
      onRefine(recommendationsMatchingCriteria);
    }
  };

  return {
    searchMode,
    isRecommendationsMode,
    handleSearchModeChange,
    handleRefineRecommendations,
    recommendationsMatchingCriteria,
  };
}
